import fp from 'lodash/fp';
import { createTheme, ThemeOptions } from '@mui/material/styles';
import type { } from '@mui/lab/themeAugmentation';
import { formHelperTextClasses, formLabelClasses } from '@mui/material';

import { typographyOptions } from './typography';
import { fontsOptions } from './fonts';
import { paletteOptions } from './palette';


declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        xs: false;
        sm: false;
        md: false;
        lg: false;
        xl: false;
        mobile: true;
        tablet: true;
        desktop: true;
    }
}

declare module '@mui/material' {
    interface ChipPropsVariantOverrides {
        orange: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        primary: true;
        secondary: true;
        accent: true;
        stroke: true;
    }
}

export const theme = createTheme(
    [
        typographyOptions,
        fontsOptions,
        paletteOptions,
        {
            breakpoints: {
                values: {
                    mobile: 0,
                    tablet: 480,
                    desktop: 1280,
                },
            },
            spacing: 4,
            typography: {
                button: {
                    textTransform: 'none',
                },
            },
            components: {
                MuiSvgIcon: {
                    defaultProps: {
                        fontSize: 'inherit',
                    },
                },
                MuiLoadingButton: {
                    styleOverrides: {
                        root: ({ ownerState, theme: { spacing } }) => ({
                            borderRadius: spacing(2.5),
                            fontWeight: 500,
                            ...ownerState.size === 'large' && {
                                minWidth: spacing(14),
                                height: spacing(14),
                                py: spacing(4),
                                px: ownerState.children ? spacing(7) : spacing(4),
                                fontSize: spacing(4.5),
                            },
                            ...ownerState.size === 'medium' && {
                                minWidth: spacing(12),
                                height: spacing(12),
                                py: ownerState.children ? spacing(3) : spacing(4),
                                px: ownerState.children ? spacing(5) : spacing(4),
                                fontSize: spacing(4),
                            },
                            ...ownerState.size === 'small' && {
                                minWidth: spacing(8),
                                height: spacing(8),
                                py: ownerState.children ? spacing(1) : spacing(2),
                                px: ownerState.children ? spacing(4) : spacing(2),
                                fontSize: spacing(4),
                            },
                            ...ownerState.variant === 'primary' && {
                                color: paletteOptions.palette.grayscale.shade0,
                                backgroundColor: paletteOptions.palette.grayscale.shade95,
                                '& svg, & path': {
                                    fill: paletteOptions.palette.grayscale.shade0,
                                },
                                '&:hover': {
                                    backgroundColor: paletteOptions.palette.general.primary60,
                                },
                                '&:active': {
                                    backgroundColor: paletteOptions.palette.general.primary80,
                                },
                                ...ownerState.loading && {
                                    backgroundColor: paletteOptions.palette.general.primary80,
                                },
                            },
                            ...ownerState.variant === 'accent' && {
                                color: paletteOptions.palette.grayscale.shade0,
                                backgroundColor: paletteOptions.palette.general.primary50,
                                '& svg, & path': {
                                    fill: paletteOptions.palette.grayscale.shade0,
                                },
                                '&:hover': {
                                    backgroundColor: paletteOptions.palette.general.primary60,
                                },
                                '&:active': {
                                    backgroundColor: paletteOptions.palette.general.primary80,
                                },
                                ...ownerState.loading && {
                                    backgroundColor: paletteOptions.palette.general.primary80,
                                },
                            },
                            ...ownerState.variant === 'secondary' && {
                                color: paletteOptions.palette.general.primary60,
                                backgroundColor: paletteOptions.palette.general.primary10,
                                '& svg, & path': {
                                    fill: paletteOptions.palette.general.primary60,
                                },
                                '&:hover': {
                                    backgroundColor: paletteOptions.palette.general.primary20,
                                },
                                '&:active': {
                                    backgroundColor: paletteOptions.palette.general.primary30,
                                },
                                ...ownerState.loading && {
                                    backgroundColor: paletteOptions.palette.general.primary30,
                                },
                            },
                            ...ownerState.variant === 'stroke' && {
                                color: paletteOptions.palette.grayscale.shade95,
                                backgroundColor: paletteOptions.palette.grayscale.shade0,
                                border: `1px solid ${paletteOptions.palette.grayscale.shade80}`,
                                '& svg, & path': {
                                    fill: paletteOptions.palette.grayscale.shade95,
                                },
                                '&:hover': {
                                    backgroundColor: paletteOptions.palette.grayscale.shade10,
                                },
                                '&:active': {
                                    backgroundColor: paletteOptions.palette.grayscale.shade20,
                                },
                                ...ownerState.loading && {
                                    backgroundColor: paletteOptions.palette.grayscale.shade20,
                                },
                            },
                            ...ownerState.variant === 'text' && {
                                color: paletteOptions.palette.grayscale.shade95,
                                padding: 0,
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                                '&:active': {
                                    backgroundColor: 'transparent',
                                },
                            },
                            ...ownerState.disabled && {
                                color: paletteOptions.palette.grayscale.shade85,
                                backgroundColor: paletteOptions.palette.grayscale.shade40,
                                border: 'none',
                            },
                        }),
                        loadingIndicator: ({ ownerState }) => ({
                            ...(ownerState.variant === 'primary' ||
                                ownerState.variant === 'accent') && {
                                color: paletteOptions.palette.grayscale.shade0,
                            },
                            ...ownerState.variant === 'secondary' && {
                                color: paletteOptions.palette.general.primary60,
                            },
                            ...ownerState.variant === 'stroke' && {
                                color: paletteOptions.palette.grayscale.shade95,
                            },
                        }),
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            height: '32px',
                            borderRadius: '48px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            lineHeight: '16px',
                            fontWeight: '500',
                        },
                        label: {
                            padding: 0,
                        },
                    },
                    variants: [
                        {
                            props: { variant: 'orange' },
                            style: {
                                color: paletteOptions.palette.infoCustom.orange50,
                                backgroundColor: paletteOptions.palette.grayscale.shade0,
                            },
                        },
                        {
                            props: { color: 'success' },
                            style: {
                                color: paletteOptions.palette.infoCustom.green50,
                            },
                        },
                        {
                            props: { variant: 'filled', color: 'success' },
                            style: {
                                backgroundColor: paletteOptions.palette.infoCustom.green10,
                            },
                        },
                        {
                            props: { variant: 'outlined' },
                            style: {
                                color: paletteOptions.palette.grayscale.shade100,
                                borderColor: paletteOptions.palette.grayscale.shade40,
                            },
                        },
                    ],
                },
                MuiLinearProgress: {
                    styleOverrides: {
                        root: {
                            backgroundColor: paletteOptions.palette.grayscale.shade20,
                            borderRadius: '12px',
                        },
                        bar: {
                            backgroundColor: paletteOptions.palette.general.primary50,
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '& label.Mui-focused': {
                                color: paletteOptions.palette.grayscale.shade90,
                            },
                            '& div.Mui-focused': {
                                backgroundColor: paletteOptions.palette.grayscale.shade10,
                                outline: `1px solid ${paletteOptions.palette.grayscale.shade60}`,
                            },
                        },
                    },
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: {
                            fontSize: '12px',
                            lineHeight: '16px',
                            marginTop: 0,
                            marginLeft: 0,
                            marginRight: 0,
                            [`&.${formHelperTextClasses.error}`]: {
                                color: paletteOptions.palette.infoCustom.red50,
                            },
                        },
                    },
                },
                MuiFilledInput: {
                    defaultProps: {
                        disableUnderline: true,
                    },
                    styleOverrides: {
                        root: {
                            color: paletteOptions.palette.grayscale.shade100,
                            backgroundColor: paletteOptions.palette.grayscale.shade10,
                            borderRadius: '12px',
                            fontSize: '16px',
                            lineHeight: '24px',
                            paddingRight: '16px',
                            '&:hover ': {
                                backgroundColor: paletteOptions.palette.grayscale.shade20,
                            },
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            fontSize: '16px',
                            lineHeight: '16px',
                            color: paletteOptions.palette.grayscale.shade90,
                            top: '4px',
                            paddingLeft: '4px',
                        },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            outlineOffset: '-1px',
                            [`& + .${formHelperTextClasses.root}`]: {
                                marginTop: '6px',
                                marginLeft: '16px',
                            },
                            input: {
                                paddingTop: '24px',
                                paddingRight: '16px',
                                paddingLeft: '16px',
                            },
                        },
                    },
                },
                MuiFormLabel: {
                    styleOverrides: {
                        root: {
                            [`&.${formLabelClasses.error}`]: {
                                color: paletteOptions.palette.infoCustom.red50,
                            },
                            [`& .${formLabelClasses.asterisk}`]: {
                                position: 'relative',
                                left: '-0.2em',
                            },
                        },
                    },
                },
            },
        } as ThemeOptions,
    ].reduce((r, c) => fp.merge(r, c)),
);
